import { Action, SubjectRoles } from "@/api-schema-enums";
import { FeatureFlag } from "@/config/feature-flags";
import { PermissionOperator } from "@/config/navigation/types";
import { AceRoutes } from "@/modules/ace/ace.routes";
import { CustomerSapExportRoutes } from "@/modules/ace/customer-sap-export/customer-sap-export.routes";
import { CasaviContactBlacklistRoutes } from "@/modules/casavi/casavi-contact-blacklist/casavi-contact-blacklist.routes";
import { CasaviContactWhitelistRoutes } from "@/modules/casavi/casavi-contact-whitelist/casavi-contact-whitelist.routes";
import { CasaviDashboardRoutes } from "@/modules/casavi/casavi-dashboard/casavi-dashboard.routes";
import { CasaviSettingsView } from "@/modules/casavi/casavi-settings/casavi-settings.view";
import { CasaviRoutes } from "@/modules/casavi/casavi.routes";
import { FabSettingsView } from "@/modules/fab/settings/fab-settings.view";
import { FitSettingsView } from "@/modules/fit/fit-settings/fit-settings.view";
import { VehicleBuyInRoutes } from "@/modules/fab/vehicle-buy-in/vehicle-buy-in.routes";
import { VehicleValuationRoutes } from "@/modules/fab/vehicle-valuation/vehicle-valuation.routes";
import { FiaRoutes } from "@/modules/fia/fia.routes";
import { OpenPositionRoutes } from "@/modules/fia/open-position/open-position.routes";
import { LoadView as FitExternalLoadView } from "@/modules/fit/external/load/load.view";
import { MyTruckView as FitExternalMyTruckView } from "@/modules/fit/external/my-truck/my-truck.view";
import { RelocationRequestRoutes as FitExternalRelocationRequestRoutes } from "@/modules/fit/external/relocation-request/relocation-request.routes";
import { UnloadView as FitExternalUnloadView } from "@/modules/fit/external/unload/unload.view";
import { FitRoutes } from "@/modules/fit/fit.routes";
import { RelocationOrderMapView } from "@/modules/fit/internal/relocation-order/map/relocation-order-map.view";
import { RelocationOrderRoutes as FitRelocationOrderRoutes } from "@/modules/fit/internal/relocation-order/relocation-order.routes";
import { HomeView } from "@/modules/home/home.view";
import { BranchRoutes } from "@/modules/masterdata/branch/branch.routes";
import { BrandRoutes } from "@/modules/masterdata/brand/brand.routes";
import { CompanyGroupRoutes } from "@/modules/masterdata/company-group/company-group.routes";
import { CompanyRoutes } from "@/modules/masterdata/company/company.routes";
import { CustomerRoutes } from "@/modules/masterdata/customer/customer.routes";
import { MasterdataDashboardRoutes } from "@/modules/masterdata/dashboard/masterdata-dashboard.routes";
import { DepartmentRoutes } from "@/modules/masterdata/department/department.routes";
import { JobFunctionRoutes } from "@/modules/masterdata/job-function/job-function.routes";
import { MasterdataRoutes } from "@/modules/masterdata/masterdata.routes";
import { ModelFamilyRoutes } from "@/modules/masterdata/model-family/model-family.routes";
import { ModelGenerationRoutes } from "@/modules/masterdata/model-generation/model-generation.routes";
import { OccupationRoutes } from "@/modules/masterdata/occupation/occupation.routes";
import { UserRoutes } from "@/modules/masterdata/user/user.routes";
import { VehicleRoutes } from "@/modules/masterdata/vehicle/vehicle.routes";
import { NotificationView } from "@/modules/notification/notification.view";
import { PhonebookRoutes } from "@/modules/phonebook/phonebook.routes";
import { ApplicationRoutes } from "@/modules/ram/application/application.routes";
import { RamDashboardView } from "@/modules/ram/dashboard/ram-dashboard.view";
import { GroupRoutes } from "@/modules/ram/group/group.routes";
import { RamRoutes } from "@/modules/ram/ram.routes";
import { CreateView as RamTemporaryCreateView } from "@/modules/ram/temporary/create/create.view";
import { ManageView as RamTemporaryManageView } from "@/modules/ram/temporary/temporary-manage.view";
import { RequestView as RamTemporaryRequestView } from "@/modules/ram/temporary/temporary-request.view";
import { TemporaryRoutes } from "@/modules/ram/temporary/temporary.routes";
import { UserSettingsRoutes } from "@/modules/user-settings/user-settings.routes";
import { ComingSoonView } from "@/views/common/comingsoon.view";
import { LoaderPreviewView } from "@/views/common/loader-preview.view";
import { ForbiddenView } from "@/views/common/forbidden.view";
import i18next from "i18next";
import {
  AppWindowIcon,
  ArrowDownToLineIcon,
  ArrowUpFromLineIcon,
  BellIcon,
  BookUserIcon,
  BriefcaseIcon,
  Building2Icon,
  BuildingIcon,
  Car,
  CarFrontIcon,
  CloudOff,
  ContactIcon,
  GaugeIcon,
  GraduationCapIcon,
  HandCoinsIcon,
  HomeIcon,
  LandmarkIcon,
  LayersIcon,
  ListChecks,
  ListTodoIcon,
  ListX,
  MapIcon,
  MapPinPen,
  Network,
  ReceiptEuroIcon,
  ScaleIcon,
  ScrollTextIcon,
  SettingsIcon,
  ShieldIcon,
  Store,
  TagIcon,
  TimerIcon,
  TruckIcon,
  UserIcon,
  WrenchIcon,
} from "lucide-react";
import { Redirect } from "wouter";
import type { NavigationItem } from "./types";

const localDevServerOnlyItems: NavigationItem[] = import.meta.env.DEV
  ? [
      {
        id: "loader-preview",
        title: () => "Loader Preview",
        routePath: "/loader-preview",
        component: LoaderPreviewView,
        audience: SubjectRoles.Int,
        hideFromSidebar: true,
      },
    ]
  : [];

const breadcrumbOnlyItems: NavigationItem[] = [
  {
    id: "breadcrumb-home",
    title: () => i18next.t("common:Home"),
    breadcrumbSegment: "home",
    breadcrumbLabel: (t) => t("common:Home"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-ram",
    title: () => "RAM",
    breadcrumbSegment: "ram",
    breadcrumbLabel: () => "RAM",
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-phonebook",
    title: () => i18next.t("phonebook:Phonebook"),
    breadcrumbSegment: "phonebook",
    breadcrumbLabel: (t) => t("phonebook:Phonebook"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-create",
    title: () => i18next.t("common:Create"),
    breadcrumbSegment: "create",
    breadcrumbLabel: (t) => t("common:Create"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-update",
    title: () => i18next.t("common:Update"),
    breadcrumbSegment: "update",
    breadcrumbLabel: (t) => t("common:Update"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-manually",
    title: () => i18next.t("common:Manually"),
    breadcrumbSegment: "manually",
    breadcrumbLabel: (t) => t("common:Manually"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-application",
    title: () => i18next.t("ram:Apps"),
    breadcrumbSegment: "application",
    breadcrumbLabel: (t) => t("ram:Apps"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-app-role",
    title: () => i18next.t("ram:AppRoles"),
    breadcrumbSegment: "app-role",
    breadcrumbLabel: (t) => t("ram:AppRoles"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-group",
    title: () => i18next.t("ram:Groups"),
    breadcrumbSegment: "group",
    breadcrumbLabel: (t) => t("ram:Groups"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-user",
    title: () => i18next.t("ram:Users"),
    breadcrumbSegment: "user",
    breadcrumbLabel: (t) => t("ram:Users"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-rule",
    title: () => i18next.t("ram:Rule"),
    breadcrumbSegment: "rule",
    breadcrumbLabel: (t) => t("ram:Rule"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-attribute",
    title: () => i18next.t("ram:AddAppRoleAttribute"),
    breadcrumbSegment: "attribute",
    breadcrumbLabel: (t) => t("ram:AddAppRoleAttribute"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-temporary",
    title: () => i18next.t("ram:TemporaryPermissions"),
    breadcrumbSegment: "temporary",
    breadcrumbLabel: (t) => t("ram:TemporaryPermissions"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-requests",
    title: () => i18next.t("common:Requests"),
    breadcrumbSegment: "requests",
    breadcrumbLabel: (t) => t("common:Requests"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-manage",
    title: () => i18next.t("masterdata:Manage"),
    breadcrumbSegment: "manage",
    breadcrumbLabel: (t) => t("masterdata:Manage"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-masterdata",
    title: () => i18next.t("masterdata:Masterdata"),
    breadcrumbSegment: "masterdata",
    breadcrumbLabel: (t) => t("masterdata:Masterdata"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-customer-sap-export",
    title: () => i18next.t("aceSapExportIssues:SapExports"),
    breadcrumbSegment: "customer-sap-export",
    breadcrumbLabel: (t) => t("aceSapExportIssues:SapExports"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-brand",
    title: () => i18next.t("ram:Brands"),
    breadcrumbSegment: "brand",
    breadcrumbLabel: (t) => t("ram:Brands"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-model-generation",
    title: () => i18next.t("common:Models"),
    breadcrumbSegment: "model-generation",
    breadcrumbLabel: (t) => t("common:Models"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-model-family",
    title: () => i18next.t("common:ModelFamilies"),
    breadcrumbSegment: "model-family",
    breadcrumbLabel: (t) => t("common:ModelFamilies"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-branch",
    title: () => i18next.t("common:Branches"),
    breadcrumbSegment: "branch",
    breadcrumbLabel: (t) => t("common:Branches"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-company-group",
    title: () => i18next.t("masterdata:CompanyGroups"),
    breadcrumbSegment: "company-group",
    breadcrumbLabel: (t) => t("masterdata:CompanyGroups"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-company",
    title: () => i18next.t("common:Companies"),
    breadcrumbSegment: "company",
    breadcrumbLabel: (t) => t("common:Companies"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-city",
    title: () => i18next.t("masterdata:Cities"),
    breadcrumbSegment: "city",
    breadcrumbLabel: (t) => t("masterdata:Cities"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-department",
    title: () => i18next.t("ram:Departments"),
    breadcrumbSegment: "department",
    breadcrumbLabel: (t) => t("ram:Departments"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-job-function",
    title: () => i18next.t("ram:JobFunctions"),
    breadcrumbSegment: "job-function",
    breadcrumbLabel: (t) => t("ram:JobFunctions"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-occupation",
    title: () => i18next.t("ram:Occupations"),
    breadcrumbSegment: "occupation",
    breadcrumbLabel: (t) => t("ram:Occupations"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-country",
    title: () => i18next.t("masterdata:Countries"),
    breadcrumbSegment: "country",
    breadcrumbLabel: (t) => t("masterdata:Countries"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-postal-code",
    title: () => i18next.t("masterdata:PostalCodes"),
    breadcrumbSegment: "postal-code",
    breadcrumbLabel: (t) => t("masterdata:PostalCodes"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-address",
    title: () => i18next.t("masterdata:Addresses"),
    breadcrumbSegment: "address",
    breadcrumbLabel: (t) => t("masterdata:Addresses"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-fit",
    title: () => "FIT",
    breadcrumbSegment: "fit",
    breadcrumbLabel: () => "FIT",
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-vehicle-valuation",
    title: () => i18next.t("fab:VehicleValuations"),
    breadcrumbSegment: "vehicle-valuation",
    breadcrumbLabel: (t) => t("fab:VehicleValuations"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-confirm",
    title: () => i18next.t("common:Confirm"),
    breadcrumbSegment: "confirm",
    breadcrumbLabel: (t) => t("common:Confirm"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-cas",
    title: () => "CAS",
    breadcrumbSegment: "cas",
    breadcrumbLabel: () => "CAS",
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-dashboard",
    title: () => i18next.t("common:Dashboard"),
    breadcrumbSegment: "dashboard",
    breadcrumbLabel: (t) => t("common:Dashboard"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-contact-blacklist",
    title: () => i18next.t("casavi:ContactBlacklist"),
    breadcrumbSegment: "contact-blacklist",
    breadcrumbLabel: (t) => t("casavi:ContactBlacklist"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-contact-whitelist",
    title: () => i18next.t("casavi:ContactWhitelist"),
    breadcrumbSegment: "contact-whitelist",
    breadcrumbLabel: (t) => t("casavi:ContactWhitelist"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-settings",
    title: () => i18next.t("casavi:CasaviSettings"),
    breadcrumbSegment: "settings",
    breadcrumbLabel: (t) => t("casavi:CasaviSettings"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-internal",
    title: () => i18next.t("ram:Intern"),
    breadcrumbSegment: "internal",
    breadcrumbLabel: (t) => t("ram:Intern"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-external",
    title: () => i18next.t("fit:Extern"),
    breadcrumbSegment: "external",
    breadcrumbLabel: (t) => t("fit:Extern"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-vehicle",
    title: () => i18next.t("common:Vehicles"),
    breadcrumbSegment: "vehicle",
    breadcrumbLabel: (t) => t("common:Vehicles"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-relocation-order",
    title: () => i18next.t("common:RelocationOrders"),
    breadcrumbSegment: "relocation-order",
    breadcrumbLabel: (t) => t("common:RelocationOrders"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-relocation-request",
    title: () => i18next.t("fit:TransportRequests"),
    breadcrumbSegment: "relocation-request",
    breadcrumbLabel: (t) => t("fit:TransportRequests"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-initialize",
    title: () => i18next.t("common:Initialize"),
    breadcrumbSegment: "initialize",
    breadcrumbLabel: (t) => t("common:Initialize"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-init",
    title: () => i18next.t("fit:NewRelocationOrder"),
    breadcrumbSegment: "init",
    breadcrumbLabel: (t) => t("fit:NewRelocationOrder"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-select-vehicle",
    title: () => i18next.t("fit:SelectVehicle"),
    breadcrumbSegment: "select-vehicle",
    breadcrumbLabel: (t) => t("fit:SelectVehicle"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-select-order-type",
    title: () => i18next.t("fit:SelectOrderType"),
    breadcrumbSegment: "select-order-type",
    breadcrumbLabel: (t) => t("fit:SelectOrderType"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-set-origin-external-address",
    title: () => i18next.t("fit:SetOriginAddress"),
    breadcrumbSegment: "set-origin-external-address",
    breadcrumbLabel: (t) => t("fit:SetOriginAddress"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-select-destination-branch",
    title: () => i18next.t("fit:SelectDestinationBranch"),
    breadcrumbSegment: "select-destination-branch",
    breadcrumbLabel: (t) => t("fit:SelectDestinationBranch"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-set-destination-external-address",
    title: () => i18next.t("fit:SetDestinationAddress"),
    breadcrumbSegment: "set-destination-external-address",
    breadcrumbLabel: (t) => t("fit:SetDestinationAddress"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-set-details",
    title: () => i18next.t("fit:SetDetails"),
    breadcrumbSegment: "set-details",
    breadcrumbLabel: (t) => t("fit:SetDetails"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-bca-create",
    title: () => i18next.t("fit:BcaRelocationOrderTitle"),
    breadcrumbSegment: "bca-create",
    breadcrumbLabel: (t) => t("fit:BcaRelocationOrderTitle"),
    hideFromSidebar: true,
    navigable: false,
  },

  {
    id: "breadcrumb-load",
    title: () => i18next.t("common:Load"),
    breadcrumbSegment: "load",
    breadcrumbLabel: (t) => t("common:Load"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-my-truck",
    title: () => i18next.t("common:MyTruck"),
    breadcrumbSegment: "my-truck",
    breadcrumbLabel: (t) => t("common:MyTruck"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-unload",
    title: () => i18next.t("common:Unload"),
    breadcrumbSegment: "unload",
    breadcrumbLabel: (t) => t("common:Unload"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-new-date",
    title: () => i18next.t("common:NewPickupDate"),
    breadcrumbSegment: "new-date",
    breadcrumbLabel: (t) => t("common:NewPickupDate"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-invite-guest",
    title: () => i18next.t("masterdata:InviteGuest"),
    breadcrumbSegment: "invite-guest",
    breadcrumbLabel: (t) => t("masterdata:InviteGuest"),
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "breadcrumb-fab",
    title: () => "FAB",
    breadcrumbSegment: "fab",
    breadcrumbLabel: () => "FAB",
    hideFromSidebar: true,
    navigable: false,
  },
];

export const navigationConfig: NavigationItem[] = [
  ...breadcrumbOnlyItems,
  {
    id: "home-root",
    title: () => i18next.t("common:Home"),
    routePath: "/",
    component: () => <Redirect to="/home" replace />,
    audience: SubjectRoles.Int,
    hideFromSidebar: true,
    navigable: false,
  },
  {
    id: "home",
    title: () => i18next.t("common:Home"),
    routePath: "/home",
    component: HomeView,
    audience: SubjectRoles.Int,
    icon: HomeIcon,
  },
  {
    id: "notification",
    title: () => i18next.t("notification:Notifications"),
    routePath: "/notification",
    component: () => <NotificationView />,
    audience: SubjectRoles.Int,
    icon: BellIcon,
    featureFlag: FeatureFlag.Notification,
    permission: { appRole: SubjectRoles.Int },
  },
  {
    id: "user-settings",
    title: () => i18next.t("common:Settings"),
    routePath: "/settings",
    component: UserSettingsRoutes,
    icon: SettingsIcon,
    nest: true,
    hideFromSidebar: true,
  },
  {
    id: "phonebook",
    title: () => i18next.t("phonebook:Phonebook"),
    routePath: "/phonebook",
    component: PhonebookRoutes,
    audience: SubjectRoles.Int,
    icon: BookUserIcon,
    nest: true,
    permission: { appRole: SubjectRoles.Int },
    videoTutorialUrl:
      "https://ahgbhg.sharepoint.com/:l:/s/SoftwareEngineering/FHckPNAQRkxFnKMj_MSNEaoBWgGnL7pElmVySqv8bVLHpw?e=9b38p9",
  },
  {
    id: "ram",
    title: () => "RAM",
    icon: ShieldIcon,
    routePath: "/ram",
    component: RamRoutes,
    audience: SubjectRoles.Int,
    nest: true,
    permission: { appRole: SubjectRoles.Int },
    navigable: false,
    videoTutorialUrl:
      "https://ahgbhg.sharepoint.com/:l:/s/SoftwareEngineering/FD_L-qPRhHpPjSTttLsPfO4BR2tG__JIq8APUZ6DHOmcNg?e=a50V1m",
    children: [
      {
        id: "ram-dashboard",
        title: () => i18next.t("common:Dashboard"),
        routePath: "/ram/dashboard",
        icon: GaugeIcon,
        permission: { appRole: SubjectRoles.Int },
        component: RamDashboardView,
      },
      {
        id: "ram-application",
        title: () => i18next.t("ram:Apps"),
        routePath: "/ram/application",
        icon: AppWindowIcon,
        permission: { action: Action.Read, subject: "Application" },
        component: ApplicationRoutes,
        nest: true,
        commands: [
          {
            id: "ram-application-create",
            title: () => i18next.t("ram:CreateApp"),
            to: "/ram/application/create",
            permission: { action: Action.Manage, subject: "Application" },
          },
        ],
        children: [
          {
            id: "ram-application-app-role",
            title: () => i18next.t("ram:AppRoles"),
            breadcrumbSegment: "app-role",
            breadcrumbDisableUuidDetails: true,
            hideFromSidebar: true,
          },
        ],
      },
      {
        id: "ram-group",
        title: () => i18next.t("ram:Groups"),
        routePath: "/ram/group",
        icon: ScaleIcon,
        permission: { action: Action.Read, subject: "Group" },
        component: GroupRoutes,
        nest: true,
        commands: [
          {
            id: "ram-group-create",
            title: () => i18next.t("ram:CreateGroup"),
            to: "/ram/group/create",
            permission: { action: Action.Manage, subject: "Group" },
          },
        ],
        children: [
          {
            id: "ram-group-app-role",
            title: () => i18next.t("ram:AppRoles"),
            breadcrumbSegment: "app-role",
            breadcrumbDisableUuidDetails: true,
            hideFromSidebar: true,
          },
          {
            id: "ram-group-rule",
            title: () => i18next.t("ram:Rules"),
            breadcrumbSegment: "rule",
            breadcrumbDisableUuidDetails: true,
            hideFromSidebar: true,
          },
        ],
      },
      {
        id: "ram-temporary",
        title: () => i18next.t("ram:TemporaryPermissions"),
        icon: TimerIcon,
        routePath: "/ram/temporary",
        navigable: false,
        component: TemporaryRoutes,
        nest: true,
        children: [
          {
            id: "ram-temporary-requests",
            title: () => i18next.t("common:MyRequests"),
            routePath: "/ram/temporary/requests",
            icon: ScrollTextIcon,
            component: RamTemporaryRequestView,
            nest: true,
            commands: [
              {
                id: "ram-temporary-request-create",
                title: () => i18next.t("ram:RequestTemporaryPermissions"),
                to: "/ram/temporary/requests/create",
              },
            ],
          },
          {
            id: "ram-temporary-create",
            title: () => i18next.t("common:Create"),
            routePath: "/ram/temporary/requests/create",
            hideFromSidebar: true,
            component: RamTemporaryCreateView,
            nest: true,
          },
          {
            id: "ram-temporary-manage",
            title: () => i18next.t("masterdata:Manage"),
            routePath: "/ram/temporary/manage",
            icon: ListTodoIcon,
            permission: {
              action: Action.Manage,
              subject: "TemporaryPermission",
            },
            component: RamTemporaryManageView,
            nest: true,
          },
        ],
      },
    ],
  },
  {
    id: "fab",
    title: () => "FAB",
    icon: ScaleIcon,
    routePath: "/fab",
    audience: SubjectRoles.Int,
    navigable: false,
    permission: {
      operator: PermissionOperator.OR,
      permissions: [
        { action: Action.Read, subject: "VehicleBuyIn" },
        { appRole: SubjectRoles.FabVehicleValuationAdministrator },
        { appRole: SubjectRoles.FabVehicleValuationApprover },
        { appRole: SubjectRoles.FabVehicleValuationUser },
      ],
    },
    children: [
      {
        id: "fab-list",
        title: () => i18next.t("fab:VehicleValuations"),
        routePath: "/fab/list",
        component: VehicleValuationRoutes,
        icon: ScaleIcon,
        permission: {
          operator: PermissionOperator.OR,
          permissions: [
            { appRole: SubjectRoles.FabVehicleValuationAdministrator },
            { appRole: SubjectRoles.FabVehicleValuationApprover },
            { appRole: SubjectRoles.FabVehicleValuationUser },
          ],
        },
        nest: true,
        breadcrumbDisableUuidDetails: true,
        commands: [
          {
            id: "fab-vehicle-valuation-initialize",
            title: () => i18next.t("fab:StartVehicleValuation"),
            to: "/fab/list/initialize/select-vehicle",
            permission: { action: Action.Create, subject: "VehicleValuation" },
          },
        ],
      },
      {
        id: "fab-buy-in",
        title: () => i18next.t("fab:VehicleBuyIns"),
        featureFlag: FeatureFlag.IsVehicleBuyInEnabled,
        routePath: "/fab/buy-in",
        component: VehicleBuyInRoutes,
        icon: HandCoinsIcon,
        permission: { action: Action.Read, subject: "VehicleBuyIn" },
        nest: true,
        breadcrumbDisableUuidDetails: true,
      },
      {
        id: "fab-settings",
        title: () => i18next.t("common:Settings"),
        routePath: "/fab/settings",
        icon: SettingsIcon,
        permission: {
          operator: PermissionOperator.OR,
          permissions: [
            { appRole: SubjectRoles.FabVehicleValuationAdministrator },
            { action: Action.Manage, subject: "ElectronicSignatureTemplateConfiguration" },
          ],
        },
        featureFlag: FeatureFlag.IsVehicleBuyInEnabled,
        featureFlagFallbackPermission: { appRole: SubjectRoles.FabVehicleValuationAdministrator },
        component: () => <FabSettingsView />,
      },
    ],
  },
  {
    id: "fit",
    title: () => "FIT",
    icon: CarFrontIcon,
    featureFlag: FeatureFlag.FitSidebar,
    routePath: "/fit",
    component: FitRoutes,
    audience: SubjectRoles.Int,
    nest: true,
    navigable: false,
    children: [
      // The 3 external FIT views are added for use devs to see them grouped into FIT
      // Other employees won't see them since they don't have the external roles
      {
        id: "fit-load",
        title: () => i18next.t("common:Load"),
        routePath: "/fit/load",
        icon: ArrowUpFromLineIcon,
        permission: {
          operator: PermissionOperator.OR,
          permissions: [{ appRole: SubjectRoles.FitExtHauler }, { appRole: SubjectRoles.FitExtAdministrator }],
        },
        component: () => <FitExternalLoadView />,
      },
      {
        id: "fit-my-truck",
        title: () => i18next.t("common:MyTruck"),
        routePath: "/fit/my-truck",
        icon: TruckIcon,
        permission: {
          operator: PermissionOperator.OR,
          permissions: [{ appRole: SubjectRoles.FitExtHauler }, { appRole: SubjectRoles.FitExtAdministrator }],
        },
        component: () => <FitExternalMyTruckView />,
      },
      {
        id: "fit-unload",
        title: () => i18next.t("common:Unload"),
        routePath: "/fit/unload",
        icon: ArrowDownToLineIcon,
        permission: {
          operator: PermissionOperator.OR,
          permissions: [{ appRole: SubjectRoles.FitExtHauler }, { appRole: SubjectRoles.FitExtAdministrator }],
        },
        component: () => <FitExternalUnloadView />,
      },
      {
        id: "fit-relocation-request",
        title: () => i18next.t("fit:TransportRequests"),
        routePath: "/fit/relocation-request",
        icon: MapPinPen,
        permission: { appRole: SubjectRoles.FitExtAdministrator },
        component: () => <FitExternalRelocationRequestRoutes />,
        nest: true,
        breadcrumbDisableUuidDetails: true,
      },
      {
        id: "fit-relocation-order",
        title: () => i18next.t("fit:RelocationOrders"),
        routePath: "/fit/relocation-order",
        icon: MapPinPen,
        permission: { appRole: SubjectRoles.FitIntModerator },
        component: () => <FitRelocationOrderRoutes />,
        nest: true,
        breadcrumbDisableUuidDetails: true,
        commands: [
          {
            id: "fit-relocation-order-init",
            title: () => i18next.t("fit:CreateRelocationOrder"),
            to: "/fit/relocation-order/init/select-vehicle",
          },
          {
            id: "fit-relocation-order-admin-create",
            title: () => i18next.t("fit:AdminCreateRelocationOrder"),
            to: "/fit/relocation-order/create/select-vehicle",
            icon: WrenchIcon,
            permission: { action: Action.Manage, subject: "RelocationOrder" },
          },
        ],
      },
      {
        id: "fit-relocation-order-map",
        title: () => i18next.t("fit:TransitMap"),
        routePath: "/fit/map",
        icon: MapIcon,
        breadcrumbLabel: (t) => t("fit:TransitMap"),
        permission: { appRole: SubjectRoles.FitIntModerator },
        component: () => <RelocationOrderMapView />,
      },
      {
        id: "fit-settings",
        title: () => i18next.t("common:Settings"),
        routePath: "/fit/settings",
        icon: SettingsIcon,
        permission: { action: Action.Manage, subject: "FitSettings" },
        component: () => <FitSettingsView />,
      },
    ],
  },
  {
    id: "fit-external",
    title: () => "FIT",
    audience: SubjectRoles.Ext,
    featureFlag: FeatureFlag.FitSidebar,
    hideFromSidebar: true,
    navigable: false,
    breadcrumbSegment: "fit",
    hiddenFromBreadcrumb: true,
    children: [
      {
        id: "fit-external-load",
        title: () => i18next.t("common:Load"),
        routePath: "/fit/load",
        icon: ArrowUpFromLineIcon,
        permission: {
          operator: PermissionOperator.OR,
          permissions: [{ appRole: SubjectRoles.FitExtHauler }, { appRole: SubjectRoles.FitExtAdministrator }],
        },
        component: () => <FitExternalLoadView />,
      },
      {
        id: "fit-external-my-truck",
        title: () => i18next.t("common:MyTruck"),
        routePath: "/fit/my-truck",
        icon: TruckIcon,
        permission: {
          operator: PermissionOperator.OR,
          permissions: [{ appRole: SubjectRoles.FitExtHauler }, { appRole: SubjectRoles.FitExtAdministrator }],
        },
        component: () => <FitExternalMyTruckView />,
      },
      {
        id: "fit-external-unload",
        title: () => i18next.t("common:Unload"),
        routePath: "/fit/unload",
        icon: ArrowDownToLineIcon,
        permission: {
          operator: PermissionOperator.OR,
          permissions: [{ appRole: SubjectRoles.FitExtHauler }, { appRole: SubjectRoles.FitExtAdministrator }],
        },
        component: () => <FitExternalUnloadView />,
      },
      {
        id: "fit-external-relocation-request",
        title: () => i18next.t("fit:TransportRequests"),
        routePath: "/fit/relocation-request",
        icon: MapPinPen,
        permission: { appRole: SubjectRoles.FitExtAdministrator },
        component: () => <FitExternalRelocationRequestRoutes />,
        nest: true,
        breadcrumbDisableUuidDetails: true,
      },
    ],
  },
  {
    id: "casavi",
    title: () => "CAS",
    icon: Building2Icon,
    routePath: "/cas",
    component: CasaviRoutes,
    audience: SubjectRoles.Int,
    nest: true,
    permission: { appRole: SubjectRoles.Int },
    navigable: false,
    children: [
      {
        id: "casavi-dashboard",
        title: () => i18next.t("common:Dashboard"),
        routePath: "/cas/dashboard",
        icon: GaugeIcon,
        permission: { appRole: SubjectRoles.CasaviAdministrator },
        component: CasaviDashboardRoutes,
        nest: true,
      },
      {
        id: "casavi-contact-whitelist",
        title: () => i18next.t("casavi:ContactWhitelist"),
        routePath: "/cas/contact-whitelist",
        icon: ListChecks,
        permission: { action: Action.Read, subject: "CasaviContactWhitelist" },
        component: CasaviContactWhitelistRoutes,
        nest: true,
        breadcrumbDisableUuidDetails: true,
        commands: [
          {
            id: "casavi-contact-whitelist-create",
            title: () => i18next.t("casavi:AddContactToWhitelist"),
            to: "/cas/contact-whitelist/create",
            permission: { action: Action.Manage, subject: "CasaviContactWhitelist" },
          },
        ],
      },
      {
        id: "casavi-contact-blacklist",
        title: () => i18next.t("casavi:ContactBlacklist"),
        routePath: "/cas/contact-blacklist",
        icon: ListX,
        permission: { action: Action.Read, subject: "CasaviContactBlacklist" },
        component: CasaviContactBlacklistRoutes,
        nest: true,
        breadcrumbDisableUuidDetails: true,
        commands: [
          {
            id: "casavi-contact-blacklist-create",
            title: () => i18next.t("casavi:CreateNewBlacklistEntry"),
            to: "/cas/contact-blacklist/create",
            permission: { action: Action.Manage, subject: "CasaviContactBlacklist" },
          },
        ],
      },
      {
        id: "casavi-settings",
        title: () => i18next.t("common:Settings"),
        routePath: "/cas/settings",
        icon: SettingsIcon,
        permission: { action: Action.Read, subject: "CasaviSettings" },
        component: () => <CasaviSettingsView />,
      },
    ],
  },
  {
    id: "ace",
    title: () => "ACE",
    icon: ContactIcon,
    routePath: "/ace",
    component: AceRoutes,
    audience: SubjectRoles.Int,
    nest: true,
    navigable: false,
    permission: { action: Action.Read, subject: "CustomerSapExport" },
    children: [
      {
        id: "ace-customer-sap-export",
        title: () => i18next.t("aceSapExportIssues:SapExports"),
        routePath: "/ace/customer-sap-export",
        icon: CloudOff,
        component: CustomerSapExportRoutes,
        audience: SubjectRoles.Int,
        nest: true,
        permission: { action: Action.Read, subject: "CustomerSapExport" },
        breadcrumbDisableUuidDetails: true,
      },
    ],
  },
  {
    id: "fia",
    title: () => "FIA",
    icon: LandmarkIcon,
    routePath: "/fia",
    component: FiaRoutes,
    audience: SubjectRoles.Int,
    nest: true,
    navigable: false,
    permission: {
      operator: PermissionOperator.OR,
      permissions: [{ appRole: SubjectRoles.FiaUser }, { appRole: SubjectRoles.MasterdataAdministrator }],
    },
    children: [
      {
        id: "fia-open-position",
        title: () => i18next.t("fia:OpenPositions"),
        routePath: "/fia/open-position",
        icon: ReceiptEuroIcon,
        component: OpenPositionRoutes,
        audience: SubjectRoles.Int,
        nest: true,
        permission: {
          operator: PermissionOperator.OR,
          permissions: [{ appRole: SubjectRoles.FiaUser }, { appRole: SubjectRoles.MasterdataAdministrator }],
        },
      },
    ],
  },
  {
    id: "masterdata",
    title: () => i18next.t("masterdata:Masterdata"),
    icon: LayersIcon,
    routePath: "/masterdata",
    component: MasterdataRoutes,
    audience: SubjectRoles.Int,
    nest: true,
    permission: { appRole: SubjectRoles.Int },
    navigable: false,
    children: [
      {
        id: "masterdata-dashboard",
        title: () => i18next.t("common:Dashboard"),
        routePath: "/masterdata/dashboard",
        icon: GaugeIcon,
        permission: { appRole: SubjectRoles.Int },
        component: MasterdataDashboardRoutes,
        nest: true,
      },
      {
        id: "masterdata-organization",
        title: () => i18next.t("common:Organization"),
        icon: Building2Icon,
        routePath: "/masterdata/organization",
        navigable: false,
        children: [
          {
            id: "masterdata-user",
            title: () => i18next.t("ram:Users"),
            routePath: "/masterdata/user",
            icon: UserIcon,
            permission: { action: Action.Read, subject: "User" },
            component: UserRoutes,
            nest: true,
            commands: [
              {
                id: "masterdata-user-invite-guest",
                title: () => i18next.t("masterdata:InviteGuest"),
                to: "/masterdata/user/invite-guest",
                permission: { action: Action.Manage, subject: "User" },
              },
            ],
          },
          {
            id: "masterdata-department",
            title: () => i18next.t("ram:Departments"),
            routePath: "/masterdata/department",
            icon: BuildingIcon,
            permission: { action: Action.Read, subject: "Department" },
            component: DepartmentRoutes,
            nest: true,
            breadcrumbDisableUuidDetails: true,
            commands: [
              {
                id: "masterdata-department-create",
                title: () => i18next.t("masterdata:CreateDepartment"),
                to: "/masterdata/department/create",
                permission: { action: Action.Manage, subject: "Department" },
              },
            ],
          },
          {
            id: "masterdata-occupation",
            title: () => i18next.t("ram:Occupations"),
            routePath: "/masterdata/occupation",
            icon: GraduationCapIcon,
            permission: { action: Action.Read, subject: "Occupation" },
            component: OccupationRoutes,
            nest: true,
            commands: [
              {
                id: "masterdata-occupation-create",
                title: () => i18next.t("masterdata:CreateOccupation"),
                to: "/masterdata/occupation/create",
                permission: { action: Action.Manage, subject: "Occupation" },
              },
            ],
          },
          {
            id: "masterdata-branch",
            title: () => i18next.t("common:Branches"),
            routePath: "/masterdata/branch",
            icon: Store,
            permission: { action: Action.Read, subject: "Branch" },
            component: BranchRoutes,
            nest: true,
            breadcrumbDisableUuidDetails: true,
            commands: [
              {
                id: "masterdata-branch-create",
                title: () => i18next.t("masterdata:CreateBranch"),
                to: "/masterdata/branch/create",
                permission: { action: Action.Manage, subject: "Branch" },
              },
            ],
          },
          {
            id: "masterdata-company-group",
            title: () => i18next.t("masterdata:CompanyGroups"),
            routePath: "/masterdata/company-group",
            icon: Network,
            permission: { action: Action.Read, subject: "CompanyGroup" },
            component: CompanyGroupRoutes,
            nest: true,
            breadcrumbDisableUuidDetails: true,
            commands: [
              {
                id: "masterdata-company-group-create",
                title: () => i18next.t("masterdata:CreateCompanyGroup"),
                to: "/masterdata/company-group/create",
                permission: { action: Action.Manage, subject: "CompanyGroup" },
              },
            ],
          },
          {
            id: "masterdata-company",
            title: () => i18next.t("common:Companies"),
            routePath: "/masterdata/company",
            icon: Building2Icon,
            permission: { action: Action.Read, subject: "Company" },
            component: CompanyRoutes,
            nest: true,
            breadcrumbDisableUuidDetails: true,
            commands: [
              {
                id: "masterdata-company-create",
                title: () => i18next.t("masterdata:CreateCompany"),
                to: "/masterdata/company/create",
                permission: { action: Action.Manage, subject: "Company" },
              },
            ],
          },
          {
            id: "masterdata-job-function",
            title: () => i18next.t("ram:JobFunctions"),
            routePath: "/masterdata/job-function",
            icon: BriefcaseIcon,
            permission: { action: Action.Read, subject: "JobFunction" },
            component: JobFunctionRoutes,
            nest: true,
            breadcrumbDisableUuidDetails: true,
            commands: [
              {
                id: "masterdata-job-function-create",
                title: () => i18next.t("masterdata:CreateJobFunction"),
                to: "/masterdata/job-function/create",
                permission: { action: Action.Manage, subject: "JobFunction" },
              },
            ],
          },
        ],
      },
      {
        id: "masterdata-fleet",
        title: () => i18next.t("common:VehicleFleet"),
        icon: Car,
        routePath: "/masterdata/fleet",
        navigable: false,
        children: [
          {
            id: "masterdata-brand",
            title: () => i18next.t("ram:Brands"),
            routePath: "/masterdata/brand",
            icon: TagIcon,
            permission: { action: Action.Read, subject: "Brand" },
            component: BrandRoutes,
            nest: true,
            breadcrumbDisableUuidDetails: true,
            commands: [
              {
                id: "masterdata-brand-create",
                title: () => i18next.t("masterdata:CreateBrand"),
                to: "/masterdata/brand/create",
                permission: { action: Action.Manage, subject: "Brand" },
              },
            ],
          },
          {
            id: "masterdata-model-family",
            title: () => i18next.t("common:ModelFamilies"),
            routePath: "/masterdata/model-family",
            icon: ListChecks,
            permission: { action: Action.Read, subject: "ModelFamily" },
            component: ModelFamilyRoutes,
            nest: true,
            breadcrumbDisableUuidDetails: true,
            commands: [
              {
                id: "masterdata-model-family-create",
                title: () => i18next.t("masterdata:CreateModelFamily"),
                to: "/masterdata/model-family/create",
                permission: { action: Action.Manage, subject: "ModelFamily" },
              },
            ],
          },
          {
            id: "masterdata-model-generation",
            title: () => i18next.t("common:Models"),
            routePath: "/masterdata/model-generation",
            icon: CarFrontIcon,
            permission: { action: Action.Read, subject: "ModelGeneration" },
            component: ModelGenerationRoutes,
            nest: true,
            breadcrumbDisableUuidDetails: true,
            commands: [
              {
                id: "masterdata-model-generation-create",
                title: () => i18next.t("masterdata:CreateModel"),
                to: "/masterdata/model-generation/create",
                permission: { action: Action.Manage, subject: "ModelGeneration" },
              },
            ],
          },
          {
            id: "masterdata-vehicle",
            title: () => i18next.t("common:Vehicles"),
            routePath: "/masterdata/vehicle",
            icon: Car,
            permission: { action: Action.Read, subject: "Vehicle" },
            component: VehicleRoutes,
            nest: true,
            breadcrumbDisableUuidDetails: true,
            commands: [
              {
                id: "masterdata-vehicle-create",
                title: () => i18next.t("masterdata:CreateVehicle"),
                to: "/masterdata/vehicle/create",
                permission: { action: Action.Manage, subject: "Vehicle" },
              },
            ],
          },
        ],
      },
      {
        id: "masterdata-customer",
        title: () => i18next.t("masterdata:Customers"),
        icon: ContactIcon,
        routePath: "/masterdata/customer",
        permission: { action: Action.Read, subject: "Customer" },
        component: CustomerRoutes,
        nest: true,
        breadcrumbDisableUuidDetails: true,
      },
    ],
  },
  {
    id: "forbidden",
    title: () => i18next.t("common:Forbidden"),
    routePath: "/forbidden",
    component: ForbiddenView,
    audience: SubjectRoles.Int,
    hideFromSidebar: true,
  },
  {
    id: "comingsoon",
    title: () => i18next.t("common:ComingSoon"),
    routePath: "/comingsoon",
    component: ComingSoonView,
    audience: SubjectRoles.Int,
    hideFromSidebar: true,
  },
  ...localDevServerOnlyItems,
];
